import {
    useContext,
    MouseEvent,
    FC,
    memo,
    ReactNode,
    PropsWithChildren
} from 'react';
import { useMutation } from '@apollo/client';
// import { InfoPanel } from '@zoopla-eng/zds/info-panel';
import { parse, stringify } from 'querystring';
import { useSelector } from 'react-redux';

import { filtersValuesSelector } from '@zoopla/redux-reducer-filters';
import { polyencSelector } from '@zoopla/redux-reducer-search-results';
import { SearchContext } from '@zoopla/listing-search-context';
import { constructEditSavedSearchFields } from '@zoopla/listing-search-utils';
import {
    EDIT_SAVED_SEARCH,
    CREATE_SAVED_SEARCH
} from '@zoopla/listing-search-queries';
import { ButtonLink, IconButton } from '@zoopla-eng/zds/button';
import { Box } from '@zoopla-eng/zds/box';
import { CloseSmall } from '@zoopla-eng/zds/icons';
import { InfoPanel } from '@zoopla-eng/zds/info-panel';

type Props = {
    onBannerClose?: () => void;
};

export const EditSavedSearchBanner: FC<React.PropsWithChildren<Props>> = ({
    onBannerClose
}) => {
    const {
        state: {
            userAlertId,
            savedSearchAndAlerts,
            isEditSavedSearchSuccess,
            searchType
        },
        dispatch
    } = useContext(SearchContext);
    const filters = useSelector(filtersValuesSelector);
    const polyenc = useSelector(polyencSelector);

    const onMutationError = () => {
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
            payload: false
        });
    };

    const [editSavedSearchMutation] = useMutation(EDIT_SAVED_SEARCH, {
        onCompleted: (data) => {
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: data.updateSavedSearch.status === 200
            });
        },
        onError: () => {
            onMutationError();
        }
    });

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: data.createNewSavedSearch.status === 200
            });
        },
        onError: () => {
            onMutationError();
        }
    });

    const pathAndQuery = (savedSearchAndAlerts?.uri || '').split('?');
    const query = parse(pathAndQuery[1]);
    delete query['category'];
    query['pref'] = 'savedsearchres';
    const editSavedSearchUrl = `${pathAndQuery[0]}?${stringify(query)}`;

    const onClose = () => {
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
            payload: false
        });
        if (onBannerClose) {
            onBannerClose();
        }
    };

    const onUpdateSavedSearchClick = async (evt: MouseEvent) => {
        evt.preventDefault();

        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            polyenc
        });

        editSavedSearchMutation({
            variables: {
                input
            }
        });
    };

    const onSaveAsNewSearchClicked = async (evt: MouseEvent) => {
        evt.preventDefault();

        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            createNew: true,
            polyenc
        });

        createSavedSearchMutation({
            variables: {
                input
            }
        });
    };

    if (isEditSavedSearchSuccess === null) {
        return (
            <MemoizedStyledBanner
                strapline="Saved search"
                title="You updated your search"
                text="Would you like to update your saved search to match these changes?"
            >
                <Box pt="S400" display="flex" columnGap="S400">
                    <ButtonLink
                        category="tertiary"
                        href={editSavedSearchUrl}
                        onClick={onUpdateSavedSearchClick}
                    >
                        Update
                    </ButtonLink>
                    <ButtonLink
                        category="tertiary"
                        href={
                            savedSearchAndAlerts
                                ? `${savedSearchAndAlerts.uri}&frequency=`
                                : ''
                        }
                        onClick={onSaveAsNewSearchClicked}
                    >
                        Save as new search
                    </ButtonLink>
                    <CloseButton onClick={onClose} />
                </Box>
            </MemoizedStyledBanner>
        );
    } else if (isEditSavedSearchSuccess) {
        return (
            <MemoizedStyledBanner
                strapline="Success"
                title="Your search has been saved"
                text=""
                type="success"
            >
                <CloseButton onClick={onClose} />
            </MemoizedStyledBanner>
        );
    } else if (!isEditSavedSearchSuccess) {
        return (
            <MemoizedStyledBanner
                strapline="An error occurred"
                title="Sorry, we weren't able to update your search"
                text="Would you like to try again or save as a new search"
                type="failure"
            >
                <Box pt="S400" display="flex" columnGap="S400">
                    <ButtonLink
                        category="tertiary"
                        href={editSavedSearchUrl}
                        onClick={onUpdateSavedSearchClick}
                    >
                        Update
                    </ButtonLink>
                    <ButtonLink
                        category="tertiary"
                        href={
                            savedSearchAndAlerts
                                ? `${savedSearchAndAlerts.uri}&frequency=`
                                : ''
                        }
                    >
                        Save as new search
                    </ButtonLink>
                    <CloseButton onClick={onClose} />
                </Box>
            </MemoizedStyledBanner>
        );
    }
    return null;
};

type BannerProps = {
    strapline: string;
    title: string;
    text?: string;
    type?: 'alert' | 'success' | 'failure';
    children: ReactNode;
};

const CloseButton: FC<PropsWithChildren<{ onClick: () => void }>> = ({
    onClick
}) => (
    <IconButton category="tertiary" onClick={onClick} label="close">
        {CloseSmall}
    </IconButton>
);

const MemoizedStyledBanner = memo<BannerProps>(
    ({ strapline, title, text = '', type = 'alert', children }) => {
        return (
            <Box pt="S500" pb="M300">
                <InfoPanel
                    strapline={strapline}
                    title={title}
                    text={text}
                    type={type}
                >
                    {children}
                </InfoPanel>
            </Box>
        );
    }
);
MemoizedStyledBanner.displayName = 'MemoizedStyledBanner';
